import { useCallback, useContext, useState } from 'react';
import Context, { User } from 'contextApi/UserContext';
import loginService from 'services/loginService';
import { ACCESS_TOKEN_NAME, USER_KEY_STORE } from 'constants/securityConts';
import { useHistory } from 'react-router-dom';
import { URL_USER_PAYMENT_TYPES } from 'constants/url';
import axios, { AxiosResponse } from 'axios';
import { ROLE_DROPSHIPPER } from 'constants/roleType';
import { PaymentTypesByUser } from 'interfaces/paymentTypes.interface';

export type LoginRequest = {
  username: string;
  password: string;
};

type State = {
  loading: boolean;
  error: boolean;
};

export default function useUser() {
  const [state, setState] = useState<State>({ loading: false, error: false });
  const history = useHistory();
  const context = useContext(Context);

  if (context === undefined) {
    throw new Error('useUser debe ser usado dentro de UserContextProvider');
  }

  const login = ({ username, password }: LoginRequest) => {
    setState({ loading: true, error: false });
    loginService({ username, password })
      .then((response) => {
        let err = false;
        if (response?.accessToken) {
          window.localStorage.setItem(ACCESS_TOKEN_NAME, response.accessToken);
          const userToSave = {
            firstName: response.firstName,
            userName: response.userName,
            email: response.email,
            roles: response.roles,
            isConfigurationComplete: response.isConfigurationComplete
          };
          const userRole: any = response.roles.find((role: string) => role);

          window.localStorage.setItem(USER_KEY_STORE, JSON.stringify(userToSave));
          context.setJwt(response.accessToken);
          context.setUser(userToSave);
          window.localStorage.setItem('shopName', response.shopName);
          context.setShopName(response.shopName);

          if (userRole === ROLE_DROPSHIPPER) {
            history.push('/admin/orders');
          } else {
            history.push('/admin/purchaseOrders');
          }
        } else {
          err = true;
        }
        setState({ loading: false, error: err });
      })
      .catch((err) => {
        window.localStorage.removeItem(ACCESS_TOKEN_NAME);
        window.localStorage.removeItem(USER_KEY_STORE);
        setState({ loading: false, error: true });
        console.error(err);
      });
  };

  const logout = useCallback(() => {
    window.localStorage.removeItem(ACCESS_TOKEN_NAME);
    window.localStorage.removeItem(USER_KEY_STORE);
    window.localStorage.removeItem('shopName');
    window.localStorage.removeItem('roleUser');
    context.setJwt(null);
    const newUser: User = {
      firstName: '',
      email: '',
      roles: [],
      isConfigurationComplete: false
    };

    context.setUser(newUser);
    history.push('/login');
  }, [context.setJwt]);

  const setIsConfigurationComplete = (value: boolean) => {
    const newUser: User = {
      firstName: context.user?.firstName || '',
      email: context.user?.email || '',
      roles: context.user?.roles || [],
      isConfigurationComplete: value
    };

    context.setUser(newUser);
    const userStored = JSON.parse(window.localStorage.getItem(USER_KEY_STORE) || '');
    userStored.isConfigurationComplete = true;
    window.localStorage.setItem(USER_KEY_STORE, JSON.stringify(userStored));
  };

  const getPaymentTypesByUser = async (): Promise<PaymentTypesByUser[]> => {
    return axios
      .get(URL_USER_PAYMENT_TYPES, {
        headers: {
          Authorization: `Bearer ${context.jwt}`
        }
      })
      .then((response: AxiosResponse) => {
        return response.data;
      });
  };

  return {
    isLogged: Boolean(context.jwt),
    jwt: context.jwt,
    setJwt: context.setJwt,
    isLoading: state.loading,
    login,
    logout,
    hasLoginError: state.error,
    user: context.user,
    setIsConfigurationComplete,
    getPaymentTypesByUser,
    shopName: context.shopName
  };
}
